import { apiClient } from '@/lib/api-client';
import type { ApiSuccess } from '@/lib/api-client';
import type { PackageType, PackageTypeCreateInput } from './types';

/**
 * Package Type Master API helpers — endpoint `/api/package-types`.
 *
 * The list endpoint accepts an optional `active=true` filter to restrict
 * to the rows operators can pick on the inbound dialog. Manager+ roles
 * can mutate; the row dropdown hides itself otherwise.
 */

export interface PackageTypeListMeta {
  total: number;
  page: number;
  per_page: number;
}

export interface PackageTypeListResult {
  rows: PackageType[];
  meta: PackageTypeListMeta;
}

export interface PackageTypeListParams {
  active?: boolean;
  page?: number;
  per_page?: number;
}

export const listPackageTypes = async (
  params: PackageTypeListParams = {},
): Promise<PackageTypeListResult> => {
  const res = await apiClient.get<ApiSuccess<PackageType[], PackageTypeListMeta>>(
    '/package-types',
    { params },
  );
  const rows = res.data.data ?? [];
  return {
    rows,
    meta: res.data.meta ?? {
      total: rows.length,
      page: params.page ?? 1,
      per_page: params.per_page ?? rows.length,
    },
  };
};

export const createPackageType = async (body: PackageTypeCreateInput): Promise<PackageType> => {
  const res = await apiClient.post<ApiSuccess<PackageType>>('/package-types', body);
  return res.data.data;
};

export const updatePackageType = async (
  id: string,
  body: Partial<PackageTypeCreateInput>,
): Promise<PackageType> => {
  const res = await apiClient.put<ApiSuccess<PackageType>>(`/package-types/${id}`, body);
  return res.data.data;
};

export const reorderPackageTypes = async (orderedIds: string[]): Promise<PackageType[]> => {
  const res = await apiClient.post<ApiSuccess<PackageType[]>>('/package-types/reorder', {
    ordered_ids: orderedIds,
  });
  return res.data.data;
};

/** Permanent removal. Only succeeds for a deactivated, unreferenced type. */
export const purgePackageType = async (id: string): Promise<void> => {
  await apiClient.delete(`/package-types/${id}/purge`);
};

export const deletePackageType = async (id: string): Promise<void> => {
  await apiClient.delete(`/package-types/${id}`);
};

export const activatePackageType = async (id: string): Promise<PackageType> => {
  const res = await apiClient.post<ApiSuccess<PackageType>>(`/package-types/${id}/activate`);
  return res.data.data;
};
